import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Coins, Mail, ShieldAlert, RefreshCw, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import {
  loadAdminUserSummary,
  grantCredits,
  checkIsAdmin,
  type AdminUserSummary,
} from "@/lib/admin";

export function AdminUsersPage() {
  const { user, loading: authLoading } = useAuth();
  const [rows, setRows] = useState<AdminUserSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  // null = haven't checked yet, true/false once is_admin() returned.
  // We render a 404 redirect for non-admins so the route appears not to exist
  // (the data is already protected at the RPC layer — this is UX hardening).
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await loadAdminUserSummary();
      setRows(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (authLoading || !user) return;
    let cancelled = false;
    (async () => {
      const ok = await checkIsAdmin();
      if (cancelled) return;
      setIsAdmin(ok);
      if (ok) await load();
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user?.id]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.email.toLowerCase().includes(q) ||
        r.schemas_tried.some((s) => s.includes(q)) ||
        r.plan?.toLowerCase().includes(q),
    );
  }, [rows, filter]);

  if (authLoading || isAdmin === null) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-neutral-500 text-sm">
        Loading…
      </div>
    );
  }

  if (!user) {
    return (
      <div className="mx-auto max-w-md px-4 py-16 text-center space-y-3">
        <ShieldAlert className="mx-auto h-10 w-10 text-neutral-400" />
        <h1 className="text-xl font-semibold">Sign in required</h1>
        <p className="text-sm text-neutral-600">
          The admin dashboard is only accessible to signed-in admins.
        </p>
        <Link to="/login" className="inline-block rounded-lg bg-brand text-white px-4 py-2 text-sm font-medium">
          Sign in
        </Link>
      </div>
    );
  }

  // Non-admins: redirect to home so the route appears not to exist.
  // (App.tsx has a catch-all `*` route that also bounces to "/", so this
  // mirrors what a true 404 path would do.)
  if (isAdmin === false) {
    return <Navigate to="/" replace />;
  }

  if (error) {
    return (
      <div className="mx-auto max-w-md px-4 py-16 text-center space-y-3">
        <ShieldAlert className="mx-auto h-10 w-10 text-rose-500" />
        <h1 className="text-xl font-semibold">Something went wrong</h1>
        <p className="text-sm text-neutral-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 space-y-6">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Users</h1>
          <p className="text-sm text-neutral-500">
            {rows.length} signups · {rows.filter((r) => r.total_extractions > 0).length} have tried it
            · {rows.filter((r) => r.first_extraction_welcome_sent_at).length} auto-welcomed
            · {rows.filter((r) => (r.plan ?? "free") !== "free").length} paid
          </p>
        </div>
        <div className="flex gap-2">
          <input
            type="search"
            placeholder="Filter by email, schema, plan…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="rounded-lg border border-neutral-300 px-3 py-2 text-sm w-64"
          />
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </header>

      <div className="rounded-xl border border-neutral-200 overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-xs uppercase tracking-wide text-neutral-600">
            <tr>
              <th className="text-left px-3 py-2">User</th>
              <th className="text-left px-3 py-2">Plan</th>
              <th className="text-right px-3 py-2">Extractions</th>
              <th className="text-left px-3 py-2">Schemas tried</th>
              <th className="text-left px-3 py-2">Last activity</th>
              <th className="text-right px-3 py-2">Credits</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <Row key={r.user_id} row={r} onChange={load} />
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-10 text-center text-neutral-500">
                  {loading ? "Loading…" : "No users yet."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Row({ row, onChange }: { row: AdminUserSummary; onChange: () => void }) {
  const planLabel = row.plan ?? "free";
  const tried = row.schemas_tried.length > 0 ? row.schemas_tried.join(", ") : "—";
  const lastActivity = row.last_extraction_at
    ? formatRelative(new Date(row.last_extraction_at))
    : "—";

  return (
    <tr className="border-t border-neutral-100">
      <td className="px-3 py-2">
        <div className="flex items-center gap-2 font-medium text-neutral-900">
          <span>{row.email}</span>
          {row.first_extraction_welcome_sent_at && (
            <span
              title={`Auto-welcomed ${formatRelative(new Date(row.first_extraction_welcome_sent_at))}`}
              className="inline-flex items-center gap-0.5 rounded-full bg-emerald-50 text-emerald-700 px-1.5 py-0.5 text-[10px] font-medium"
            >
              <CheckCircle2 className="h-3 w-3" /> welcomed
            </span>
          )}
        </div>
        <div className="text-xs text-neutral-500">
          Signed up {formatRelative(new Date(row.signup_at))}
        </div>
      </td>
      <td className="px-3 py-2">
        <span
          className={
            "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize " +
            (planLabel === "free"
              ? "bg-neutral-100 text-neutral-700"
              : "bg-emerald-100 text-emerald-800")
          }
        >
          {planLabel}
        </span>
        {row.subscription_status && row.subscription_status !== "active" && (
          <div className="text-xs text-neutral-500 mt-0.5 capitalize">{row.subscription_status}</div>
        )}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        <div className="font-medium">{row.completed_extractions}</div>
        {row.failed_extractions > 0 && (
          <div className="text-xs text-rose-600">{row.failed_extractions} failed</div>
        )}
      </td>
      <td className="px-3 py-2 text-xs">{tried}</td>
      <td className="px-3 py-2 text-xs text-neutral-600">{lastActivity}</td>
      <td className="px-3 py-2 text-right tabular-nums">
        <div className="inline-flex items-center gap-1 text-amber-700">
          <Coins className="h-3.5 w-3.5" />
          {row.credit_balance}
        </div>
      </td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        <RowActions row={row} onChange={onChange} />
      </td>
    </tr>
  );
}

function RowActions({ row, onChange }: { row: AdminUserSummary; onChange: () => void }) {
  const [granting, setGranting] = useState(false);

  async function handleGrant(amount: number) {
    setGranting(true);
    try {
      const note = window.prompt(
        `Grant ${amount} credits to ${row.email}? Optional note:`,
        "Manual comp",
      );
      if (note === null) {
        setGranting(false);
        return;
      }
      const newBalance = await grantCredits(row.user_id, amount, note);
      toast.success(`Granted ${amount} → new balance ${newBalance}`);
      onChange();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setGranting(false);
    }
  }

  return (
    <div className="inline-flex items-center gap-1">
      <button
        onClick={() => handleGrant(10)}
        disabled={granting}
        title="Grant 10 credits"
        className="rounded px-2 py-1 text-xs font-medium text-neutral-600 hover:bg-neutral-100 disabled:opacity-50"
      >
        +10
      </button>
      <button
        onClick={() => handleGrant(20)}
        disabled={granting}
        title="Grant 20 credits"
        className="rounded px-2 py-1 text-xs font-medium text-neutral-600 hover:bg-neutral-100 disabled:opacity-50"
      >
        +20
      </button>
      <a
        href={buildGmailComposeUrl(row)}
        target="_blank"
        rel="noreferrer"
        title={`Compose email to ${row.email} in Gmail`}
        className="rounded px-2 py-1 text-xs font-medium text-neutral-600 hover:bg-neutral-100"
      >
        <Mail className="h-3.5 w-3.5" />
      </a>
    </div>
  );
}

/**
 * Build a Gmail "compose" URL that opens a new draft in Gmail web with
 * subject + body prefilled. Uses what we know about the user — the
 * schemas they tried — to make the outreach personal-but-fast.
 *
 * Opens regardless of whether mailto: is wired up on the OS; falls back
 * cleanly if the user isn't signed into Gmail (they'll be prompted).
 */
function buildGmailComposeUrl(row: AdminUserSummary): string {
  const schemas = row.schemas_tried;
  const alreadyWelcomed = Boolean(row.first_extraction_welcome_sent_at);

  // Two outreach modes: cold (no auto-welcome sent yet) vs follow-up
  // (the auto-welcome already went out so this is a personal nudge).
  if (alreadyWelcomed) {
    const subject = "Following up on DocLens — any thoughts so far?";
    const intro =
      schemas.length > 0
        ? `You tried the ${schemas.join(", ")} extraction${schemas.length > 1 ? "s" : ""} on DocLens, and the welcome email went out automatically. I wanted to check in personally:`
        : "The welcome email went out automatically. I wanted to check in personally:";
    const body = [
      `Hey,`,
      ``,
      `Stephen from DocLens again. ${intro}`,
      ``,
      `  - Did the result match what you needed?`,
      `  - Anything specific you wished worked differently?`,
      `  - Schema you'd love to see next (W-2, bank statement, ID card, etc.)?`,
      ``,
      `Even a one-liner reply helps me prioritize what to build.`,
      ``,
      `Thanks,`,
      `Stephen`,
      `https://doclens.app`,
    ].join("\n");
    return gmailUrl(row.email, subject, body);
  }

  // Cold — first contact. Schema-agnostic so manual outreach matches the
  // auto-welcome's tone whether the recipient extracted or just signed up.
  const subject = "Quick question about your DocLens signup";
  const intro =
    schemas.length === 0
      ? "Saw you signed up for DocLens — thanks for trying it."
      : "Saw you tried a text extraction on DocLens — thanks for testing it out.";
  const body = [
    `Hey,`,
    ``,
    `I'm Stephen, the founder of DocLens. ${intro}`,
    ``,
    `I'm building this in public and would love to hear from you:`,
    ``,
    `  1. What type of document were you looking to get the text from?`,
    `  2. What were you going to do with the JSON (Excel, accounting system, custom code, something else)?  Or what output were you looking for?`,
    `  3. Was there anything that almost made you leave before you uploaded?`,
    `  4. Was there anything you wish the application did?`,
    ``,
    `Reply with even a sentence and I'll add bonus credits to your account.`,
    ``,
    `Thanks,`,
    `Stephen`,
    `https://doclens.app`,
  ].join("\n");
  return gmailUrl(row.email, subject, body);
}

function gmailUrl(to: string, subject: string, body: string): string {
  const params = new URLSearchParams({ view: "cm", fs: "1", to, su: subject, body });
  return `https://mail.google.com/mail/?${params.toString()}`;
}

function formatRelative(date: Date): string {
  const ms = Date.now() - date.getTime();
  const min = Math.round(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? "" : "s"} ago`;
  return date.toLocaleDateString();
}
